import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Anchor,
  LogOut, 
  Download, 
  RefreshCw, 
  Search,
  Calendar,
  DollarSign,
  Users,
  CheckCircle2,
  Clock,
  Loader2,
  Ship,
  MapPin,
  Phone,
  Trash2,
  UserPlus,
  Car,
  AlertTriangle,
  X,
  Filter,
  CheckSquare,
  Square,
  Eye,
  TrendingUp,
  Navigation,
  StopCircle,
  PlayCircle,
  ExternalLink,
  Copy,
  Link2,
  CreditCard,
  Banknote,
  Euro,
  Menu,
  Settings
} from "lucide-react";
import axios from "axios";

import { API_URL } from "@/config/api";

const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  on_the_way: "bg-orange-100 text-orange-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  assigned: "bg-purple-100 text-purple-800"
};

const paymentColors = {
  pending: "bg-amber-100 text-amber-800",
  paid: "bg-purple-100 text-purple-800",
  paid_by_cash: "bg-emerald-100 text-emerald-800",
  refunded: "bg-red-100 text-red-800"
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [visitorStats, setVisitorStats] = useState(null);
  const [drivers, setDrivers] = useState([]);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false);
  
  // Action states
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [deletingBooking, setDeletingBooking] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [startingTracking, setStartingTracking] = useState(null);
  // Store both client and driver tracking URLs per booking
  const [trackingData, setTrackingData] = useState({}); // { bookingId: { clientUrl, driverUrl } }
  
  // Selection state
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  
  // Filter states
  const [filters, setFilters] = useState({
    status: "all",
    payment_status: "all",
    terminal: "all",
    driver_assignment: "all",
    date_from: "",
    date_to: "",
    search: ""
  });

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build query params for filters
      const params = new URLSearchParams();
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.payment_status !== "all") params.append("payment_status", filters.payment_status);
      if (filters.terminal !== "all") params.append("terminal", filters.terminal);
      if (filters.driver_assignment !== "all") params.append("driver_assignment", filters.driver_assignment);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      
      const [bookingsRes, statsRes, driversRes, visitorRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/bookings?${params.toString()}`, { headers }),
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/admin/drivers?active_only=true`, { headers }),
        axios.get(`${API_URL}/api/admin/visitor-stats`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setBookings(bookingsRes.data.bookings);
      setStats(statsRes.data);
      setDrivers(driversRes.data.drivers || []);
      setVisitorStats(visitorRes.data);
      setSelectedBookings(new Set()); // Clear selection on refresh
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        localStorage.removeItem("admin_token");
        navigate("/admin");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate, filters]);

  // Refetch when filters change
  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [filters.status, filters.payment_status, filters.terminal, filters.driver_assignment, filters.date_from, filters.date_to]);

  // Auto-refresh every 30 seconds to sync tracking status changes from drivers
  useEffect(() => {
    if (!token) return;
    
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 30000); // 30 seconds
    
    return () => clearInterval(refreshInterval);
  }, [token, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const handleStatusUpdate = async (bookingId, field, value) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API_URL}/api/admin/bookings/${bookingId}`,
        { [field]: value },
        { headers }
      );
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update status");
    }
  };

  const handleExport = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_URL}/api/admin/export`, { headers });
      
      const blob = new Blob([response.data.csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ygo-bookings-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.count} bookings`);
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export bookings");
    }
  };

  // Single booking delete
  const handleDeleteBooking = async (bookingId) => {
    setDeletingBooking(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_URL}/api/admin/bookings/${bookingId}`, { headers });
      toast.success("Booking deleted successfully");
      setShowDeleteModal(null);
      fetchData();
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.detail || "Failed to delete booking");
    } finally {
      setDeletingBooking(false);
    }
  };

  // Single driver assignment
  const handleAssignDriver = async (bookingId, driverId) => {
    setAssigningDriver(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_URL}/api/admin/bookings/${bookingId}/assign-driver`,
        { driver_id: driverId },
        { headers }
      );
      toast.success("Driver assigned successfully");
      setShowAssignModal(null);
      fetchData();
    } catch (error) {
      console.error("Assignment failed:", error);
      toast.error(error.response?.data?.detail || "Failed to assign driver");
    } finally {
      setAssigningDriver(false);
    }
  };

  // Start tracking for a booking (driver on the way)
  const handleStartTracking = async (bookingId) => {
    setStartingTracking(bookingId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/tracking/${bookingId}/start`,
        { send_notification: true },
        { headers }
      );
      
      const { tracking_url, driver_tracking_url, driver_email_sent, client_email_sent } = response.data;
      
      // Store both URLs for this booking
      const baseUrl = window.location.origin;
      setTrackingData(prev => ({ 
        ...prev, 
        [bookingId]: {
          clientUrl: `${baseUrl}${tracking_url}`,
          driverUrl: `${baseUrl}${driver_tracking_url}`
        }
      }));
      
      if (driver_email_sent && client_email_sent) {
        toast.success("Tracking started! Driver & client notified by email");
      } else if (driver_email_sent) {
        toast.success("Tracking started! Driver notified (client email failed)");
      } else if (client_email_sent) {
        toast.success("Tracking started! Client notified (driver email failed)");
      } else {
        toast.success("Tracking started! (email notifications failed)");
      }
      
      fetchData();
    } catch (error) {
      console.error("Start tracking failed:", error);
      toast.error(error.response?.data?.detail || "Failed to start tracking");
    } finally {
      setStartingTracking(null);
    }
  };

  // Copy link to clipboard
  const copyToClipboard = async (url, type) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${type} link copied!`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(`${type} link copied!`);
    }
  };

  // Stop tracking for a booking
  const handleStopTracking = async (bookingId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_URL}/api/tracking/${bookingId}/stop`,
        {},
        { headers }
      );
      
      // Clear tracking data for this booking
      setTrackingData(prev => {
        const newData = { ...prev };
        delete newData[bookingId];
        return newData;
      });
      
      toast.success("Tracking paused - you can reactivate it later");
      fetchData();
    } catch (error) {
      console.error("Stop tracking failed:", error);
      toast.error(error.response?.data?.detail || "Failed to stop tracking");
    }
  };

  // Reactivate tracking for a booking (only if admin stopped it, not driver)
  const handleReactivateTracking = async (bookingId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/tracking/${bookingId}/reactivate`,
        {},
        { headers }
      );
      
      if (response.data.reactivated) {
        toast.success("Tracking reactivated!");
        fetchData();
      } else {
        toast.error("Cannot reactivate tracking");
      }
    } catch (error) {
      console.error("Reactivate tracking failed:", error);
      toast.error(error.response?.data?.detail || "Cannot reactivate tracking");
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedBookings.size === 0) return;
    setBulkProcessing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/admin/bookings/bulk-delete`,
        { booking_ids: Array.from(selectedBookings) },
        { headers }
      );
      
      const { total_deleted, total_failed } = response.data;
      if (total_failed > 0) {
        toast.warning(`Deleted ${total_deleted} bookings, ${total_failed} failed`);
      } else {
        toast.success(`Deleted ${total_deleted} bookings`);
      }
      setShowBulkDeleteModal(false);
      setSelectedBookings(new Set());
      fetchData();
    } catch (error) {
      console.error("Bulk delete failed:", error);
      toast.error(error.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk assign driver
  const handleBulkAssignDriver = async (driverId) => {
    if (selectedBookings.size === 0) return;
    setBulkProcessing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/admin/bookings/bulk-assign-driver`,
        { booking_ids: Array.from(selectedBookings), driver_id: driverId },
        { headers }
      );
      
      const { total_assigned, total_failed, total_skipped } = response.data;
      let message = `Assigned ${total_assigned} bookings`;
      if (total_skipped > 0) message += `, ${total_skipped} already assigned`;
      if (total_failed > 0) message += `, ${total_failed} failed`;
      
      if (total_failed > 0) {
        toast.warning(message);
      } else {
        toast.success(message);
      }
      setShowBulkAssignModal(false);
      setSelectedBookings(new Set());
      fetchData();
    } catch (error) {
      console.error("Bulk assign failed:", error);
      toast.error(error.response?.data?.detail || "Bulk assign failed");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk update status
  const handleBulkUpdateStatus = async (status) => {
    if (selectedBookings.size === 0) return;
    setBulkProcessing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/admin/bookings/bulk-update-status`,
        { booking_ids: Array.from(selectedBookings), status },
        { headers }
      );
      
      const { total_updated, total_failed } = response.data;
      if (total_failed > 0) {
        toast.warning(`Updated ${total_updated} bookings, ${total_failed} failed`);
      } else {
        toast.success(`Updated ${total_updated} bookings to ${status}`);
      }
      setShowBulkStatusModal(false);
      setSelectedBookings(new Set());
      fetchData();
    } catch (error) {
      console.error("Bulk status update failed:", error);
      toast.error(error.response?.data?.detail || "Bulk status update failed");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Bulk update payment status
  const handleBulkUpdatePaymentStatus = async (paymentStatus) => {
    if (selectedBookings.size === 0) return;
    setBulkProcessing(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_URL}/api/admin/bookings/bulk-update-payment-status`,
        { booking_ids: Array.from(selectedBookings), payment_status: paymentStatus },
        { headers }
      );
      
      const { total_updated, total_failed } = response.data;
      if (total_failed > 0) {
        toast.warning(`Updated ${total_updated} bookings, ${total_failed} failed`);
      } else {
        toast.success(`Updated ${total_updated} bookings to ${paymentStatus}`);
      }
      setShowBulkPaymentModal(false);
      setSelectedBookings(new Set());
      fetchData();
    } catch (error) {
      console.error("Bulk payment status update failed:", error);
      toast.error(error.response?.data?.detail || "Bulk payment status update failed");
    } finally {
      setBulkProcessing(false);
    }
  };

  // Selection handlers
  const toggleSelectBooking = (bookingId) => {
    const newSelected = new Set(selectedBookings);
    if (newSelected.has(bookingId)) {
      newSelected.delete(bookingId);
    } else {
      newSelected.add(bookingId);
    }
    setSelectedBookings(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedBookings.size === filteredBookings.length) {
      setSelectedBookings(new Set());
    } else {
      setSelectedBookings(new Set(filteredBookings.map(b => b.booking_id)));
    }
  };

  const resetFilters = () => {
    setFilters({
      status: "all",
      payment_status: "all",
      terminal: "all",
      driver_assignment: "all",
      date_from: "",
      date_to: "",
      search: ""
    });
  };

  const hasActiveFilters = filters.status !== "all" || 
    filters.payment_status !== "all" || 
    filters.terminal !== "all" || 
    filters.driver_assignment !== "all" ||
    filters.date_from || 
    filters.date_to;

  // Filter bookings by search (client-side for instant feedback)
  const filteredBookings = bookings.filter(booking => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      booking.booking_id?.toLowerCase().includes(searchLower) ||
      booking.passenger_name?.toLowerCase().includes(searchLower) ||
      booking.ship_name?.toLowerCase().includes(searchLower) ||
      booking.phone_number?.includes(filters.search)
    );
  });

  if (loading && bookings.length === 0) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-white">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-slate-900 text-white p-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Anchor className="w-8 h-8 text-orange-500" />
            <div>
              <h1 className="text-xl font-bold">Y GO Admin</h1>
              <p className="text-xs text-slate-400">Booking Management</p>
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/admin/drivers">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-slate-300 hover:text-white"
                data-testid="manage-drivers-btn"
              >
                <Car className="w-4 h-4 mr-2" />
                Drivers
              </Button>
            </Link>
            <Link to="/admin/clients">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-slate-300 hover:text-white"
                data-testid="manage-clients-btn"
              >
                <Users className="w-4 h-4 mr-2" />
                Clients
              </Button>
            </Link>
            <Link to="/admin/tariffs">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-slate-300 hover:text-white"
                data-testid="manage-tariffs-btn"
              >
                <Euro className="w-4 h-4 mr-2" />
                Tarifs
              </Button>
            </Link>
            <Link to="/admin/config">
              <Button 
                variant="ghost" 
                size="sm"
                className="text-slate-300 hover:text-white"
                data-testid="server-config-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Config
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchData}
              className="text-slate-300 hover:text-white"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleLogout}
              className="text-slate-300 hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
          
          {/* Mobile Hamburger Menu */}
          <div className="md:hidden flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchData}
              className="text-slate-300 hover:text-white"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-slate-300 hover:text-white"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-slate-700 space-y-2">
            <Link to="/admin/drivers" className="block">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              >
                <Car className="w-4 h-4 mr-3" />
                Drivers
              </Button>
            </Link>
            <Link to="/admin/clients" className="block">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              >
                <Users className="w-4 h-4 mr-3" />
                Clients
              </Button>
            </Link>
            <Link to="/admin/tariffs" className="block">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              >
                <Euro className="w-4 h-4 mr-3" />
                Tarifs
              </Button>
            </Link>
            <Link to="/admin/config" className="block">
              <Button 
                variant="ghost" 
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
              >
                <Settings className="w-4 h-4 mr-3" />
                Configuration
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Logout
            </Button>
          </div>
        )}
      </header>

      <div className="max-w-7xl mx-auto p-4 space-y-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Ship className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Bookings</p>
                    <p className="text-2xl font-bold">{stats.total_bookings}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-2xl font-bold">{stats.pending_bookings}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Completed</p>
                    <p className="text-2xl font-bold">{stats.completed_bookings}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Online Revenue</p>
                    <p className="text-2xl font-bold">€{stats.revenue?.online?.toFixed(0) || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Banknote className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Cash Revenue</p>
                    <p className="text-2xl font-bold">€{stats.revenue?.cash?.toFixed(0) || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Visitor Stats Cards */}
        {visitorStats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-indigo-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Eye className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Today's Visits</p>
                    <p className="text-2xl font-bold">{visitorStats.today?.total_visits || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Today's Unique</p>
                    <p className="text-2xl font-bold">{visitorStats.today?.unique_visitors || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-teal-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">All-Time Visits</p>
                    <p className="text-2xl font-bold">{visitorStats.all_time?.total_visits || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-100 rounded-lg">
                    <Users className="w-5 h-5 text-cyan-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">All-Time Unique</p>
                    <p className="text-2xl font-bold">{visitorStats.all_time?.unique_visitors || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters Section */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Filter className="w-5 h-5" />
                Filters
              </CardTitle>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-slate-500">
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {/* Search */}
              <div className="col-span-2 lg:col-span-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => setFilters(f => ({...f, search: e.target.value}))}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <Select value={filters.status} onValueChange={(v) => setFilters(f => ({...f, status: v}))}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="on_the_way">On The Way</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>

              {/* Payment Status Filter */}
              <Select value={filters.payment_status} onValueChange={(v) => setFilters(f => ({...f, payment_status: v}))}>
                <SelectTrigger data-testid="payment-filter">
                  <SelectValue placeholder="Payment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payments</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid Online</SelectItem>
                  <SelectItem value="paid_by_cash">Paid by Cash</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>

              {/* Terminal Filter */}
              <Select value={filters.terminal} onValueChange={(v) => setFilters(f => ({...f, terminal: v}))}>
                <SelectTrigger data-testid="terminal-filter">
                  <SelectValue placeholder="Terminal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Terminals</SelectItem>
                  <SelectItem value="sogestrol">Sogestrol</SelectItem>
                  <SelectItem value="jonction">Jonction</SelectItem>
                  <SelectItem value="tdf">Terminal de France (TDF)</SelectItem>
                  <SelectItem value="europe_ameriques">Europe / Amériques</SelectItem>
                  <SelectItem value="cim">CIM</SelectItem>
                  <SelectItem value="tnmsc">TNMSC</SelectItem>
                  <SelectItem value="roro">Terminal Roulier (RORO)</SelectItem>
                  <SelectItem value="multivrac">Multivrac (MTV)</SelectItem>
                </SelectContent>
              </Select>

              {/* Driver Assignment Filter */}
              <Select value={filters.driver_assignment} onValueChange={(v) => setFilters(f => ({...f, driver_assignment: v}))}>
                <SelectTrigger data-testid="driver-filter">
                  <SelectValue placeholder="Driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Drivers</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="not_assigned">Not Assigned</SelectItem>
                  {drivers.map(driver => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date From */}
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters(f => ({...f, date_from: e.target.value}))}
                className="w-full"
                data-testid="date-from-filter"
              />

              {/* Date To */}
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters(f => ({...f, date_to: e.target.value}))}
                className="w-full"
                data-testid="date-to-filter"
              />
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedBookings.size > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-medium text-blue-800">
                  {selectedBookings.size} booking{selectedBookings.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowBulkAssignModal(true)}
                    data-testid="bulk-assign-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Assign Driver
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowBulkStatusModal(true)}
                    data-testid="bulk-status-btn"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Update Status
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setShowBulkPaymentModal(true)}
                    data-testid="bulk-payment-btn"
                  >
                    <DollarSign className="w-4 h-4 mr-1" />
                    Update Payment
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => setShowBulkDeleteModal(true)}
                    data-testid="bulk-delete-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setSelectedBookings(new Set())}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bookings Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Bookings ({filteredBookings.length})
              </CardTitle>
              <Button size="sm" variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredBookings.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Ship className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No bookings found</p>
                {hasActiveFilters && (
                  <Button variant="link" onClick={resetFilters} className="mt-2">
                    Clear filters
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th className="w-10">
                        <Checkbox
                          checked={selectedBookings.size === filteredBookings.length && filteredBookings.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </th>
                      <th>Booking ID</th>
                      <th>Ship / Terminal</th>
                      <th>Pickup Address</th>
                      <th>Passenger</th>
                      <th>Driver Notes</th>
                      <th>Pickup</th>
                      <th>Vehicle Price</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Driver</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id || booking.booking_id}>
                        <td>
                          <Checkbox
                            checked={selectedBookings.has(booking.booking_id)}
                            onCheckedChange={() => toggleSelectBooking(booking.booking_id)}
                            data-testid={`select-booking-${booking.booking_id}`}
                          />
                        </td>
                        <td>
                          <span className="font-mono font-bold text-orange-500">
                            {booking.booking_id}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <Ship className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="font-medium">{booking.ship_name}</p>
                              <p className="text-xs text-slate-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {booking.terminal_name}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="max-w-[150px]">
                            <p className="text-sm truncate" title={booking.pickup_address}>
                              {booking.pickup_address || "N/A"}
                            </p>
                          </div>
                        </td>
                        <td>
                          <div>
                            <p className="font-medium">{booking.passenger_name}</p>
                            <p className="text-xs text-slate-500 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {booking.phone_number}
                            </p>
                            <p className="text-xs text-slate-400">
                              {booking.passengers} pax (info)
                            </p>
                          </div>
                        </td>
                        <td>
                          <div className="max-w-[120px]">
                            {booking.driver_notes ? (
                              <p className="text-sm text-slate-600 truncate" title={booking.driver_notes}>
                                {booking.driver_notes}
                              </p>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <p className="font-medium">
                              {new Date(booking.pickup_datetime).toLocaleDateString('en-GB')}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(booking.pickup_datetime).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            {booking.night_surcharge_applied && (
                              <Badge variant="outline" className="text-xs mt-1">Night +20%</Badge>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <p className="font-mono font-bold">€{booking.final_price?.toFixed(2)}</p>
                            <p className="text-xs text-slate-400">per vehicle</p>
                          </div>
                        </td>
                        <td>
                          <Select 
                            value={booking.payment_status}
                            onValueChange={(value) => handleStatusUpdate(booking.booking_id, "payment_status", value)}
                          >
                            <SelectTrigger className="h-8 w-28">
                              <Badge className={paymentColors[booking.payment_status] || ""}>
                                {booking.payment_status === "paid_by_cash" ? "Cash" : 
                                 booking.payment_status === "paid" ? "Online" : 
                                 booking.payment_status}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="paid">Paid Online</SelectItem>
                              <SelectItem value="paid_by_cash">Paid by Cash</SelectItem>
                              <SelectItem value="refunded">Refunded</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td>
                          <Select 
                            value={booking.status}
                            onValueChange={(value) => handleStatusUpdate(booking.booking_id, "status", value)}
                          >
                            <SelectTrigger className="h-8 w-28">
                              <Badge className={statusColors[booking.status] || ""}>
                                {booking.status === "on_the_way" ? "On The Way" : booking.status}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="confirmed">Confirmed</SelectItem>
                              <SelectItem value="assigned">Assigned</SelectItem>
                              <SelectItem value="on_the_way">On The Way</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td>
                          <div className="min-w-[100px]">
                            {booking.assigned_driver_name ? (
                              <div>
                                <p className="font-medium text-sm text-purple-700">{booking.assigned_driver_name}</p>
                                <p className="text-xs text-slate-400">
                                  {booking.assigned_at ? new Date(booking.assigned_at).toLocaleString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  }) : ''}
                                </p>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">Not assigned</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            {/* Tracking button - only show if driver is assigned */}
                            {booking.assigned_driver_id && booking.status !== "completed" && booking.status !== "cancelled" && (
                              // Use tracking_session_status to determine actual tracking state
                              // 'active' = driver is actively tracking (show Stop button)
                              // 'pending' = tracking created but driver hasn't started GPS yet
                              // 'paused' = admin paused it (show Reactivate if admin paused)
                              // null/undefined = no tracking session, show Start button
                              (booking.tracking_session_status === "active" || booking.status === "on_the_way") ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStopTracking(booking.booking_id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`stop-tracking-btn-${booking.booking_id}`}
                                  title="Pause Tracking (can reactivate)"
                                >
                                  <StopCircle className="w-4 h-4" />
                                </Button>
                              ) : booking.can_reactivate_tracking ? (
                                /* Reactivate button - shows when tracking was paused by admin */
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReactivateTracking(booking.booking_id)}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  data-testid={`reactivate-tracking-btn-${booking.booking_id}`}
                                  title="Reactivate Tracking"
                                >
                                  <PlayCircle className="w-4 h-4" />
                                </Button>
                              ) : booking.tracking_session_status === "pending" ? (
                                /* Pending = tracking session exists, waiting for driver GPS */
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  data-testid={`pending-tracking-btn-${booking.booking_id}`}
                                  title="Waiting for driver to start GPS"
                                >
                                  <Clock className="w-4 h-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleStartTracking(booking.booking_id)}
                                  disabled={startingTracking === booking.booking_id}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  data-testid={`start-tracking-btn-${booking.booking_id}`}
                                  title="Start Tracking (Driver On The Way)"
                                >
                                  {startingTracking === booking.booking_id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Navigation className="w-4 h-4" />
                                  )}
                                </Button>
                              )
                            )}
                            {/* Copy tracking links if active or pending */}
                            {(trackingData[booking.booking_id] || booking.tracking_session_status === "active" || booking.tracking_session_status === "pending" || booking.status === "on_the_way") && (
                              <div className="flex items-center gap-1">
                                {/* Copy Client Link */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const url = trackingData[booking.booking_id]?.clientUrl || 
                                      `${window.location.origin}/track/${booking.tracking_token}`;
                                    copyToClipboard(url, "Client");
                                  }}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-9 px-2"
                                  title="Copy Client Tracking Link"
                                  data-testid={`copy-client-link-${booking.booking_id}`}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  <span className="text-xs">Client</span>
                                </Button>
                                {/* Copy Driver Link */}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const url = trackingData[booking.booking_id]?.driverUrl || 
                                      `${window.location.origin}/driver/${booking.tracking_token}`;
                                    copyToClipboard(url, "Driver");
                                  }}
                                  className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 h-9 px-2"
                                  title="Copy Driver Tracking Link"
                                  data-testid={`copy-driver-link-${booking.booking_id}`}
                                >
                                  <Copy className="w-3 h-3 mr-1" />
                                  <span className="text-xs">Driver</span>
                                </Button>
                              </div>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowAssignModal(booking)}
                              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                              data-testid={`assign-driver-btn-${booking.booking_id}`}
                              title="Assign Driver"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setShowDeleteModal(booking)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-booking-btn-${booking.booking_id}`}
                              title="Delete Booking"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Single Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Deletion
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Are you sure you want to permanently delete booking <strong className="text-orange-500">{showDeleteModal.booking_id}</strong>?
              </p>
              <div className="bg-slate-50 p-3 rounded-md mb-4 text-sm">
                <p><strong>Ship:</strong> {showDeleteModal.ship_name}</p>
                <p><strong>Passenger:</strong> {showDeleteModal.passenger_name}</p>
                <p><strong>Terminal:</strong> {showDeleteModal.terminal_name}</p>
              </div>
              <p className="text-xs text-red-500 mb-4">
                This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1"
                  disabled={deletingBooking}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleDeleteBooking(showDeleteModal.booking_id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={deletingBooking}
                  data-testid="confirm-delete-btn"
                >
                  {deletingBooking ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Single Assign Driver Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-600">
                <UserPlus className="w-5 h-5" />
                Assign Driver
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Select a driver for booking <strong className="text-orange-500">{showAssignModal.booking_id}</strong>
              </p>
              <div className="bg-slate-50 p-3 rounded-md mb-4 text-sm">
                <p><strong>Ship:</strong> {showAssignModal.ship_name}</p>
                <p><strong>Terminal:</strong> {showAssignModal.terminal_name}</p>
                <p><strong>Pickup:</strong> {new Date(showAssignModal.pickup_datetime).toLocaleString('en-GB')}</p>
              </div>
              
              {drivers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-500 mb-2">No active drivers available.</p>
                  <Link to="/admin/drivers">
                    <Button variant="outline" size="sm">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Drivers
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {drivers.map((driver) => (
                    <div 
                      key={driver.id}
                      className="flex items-center justify-between p-3 border rounded-md hover:bg-slate-50 cursor-pointer"
                      onClick={() => !assigningDriver && handleAssignDriver(showAssignModal.booking_id, driver.id)}
                    >
                      <div>
                        <p className="font-medium">{driver.first_name} {driver.last_name}</p>
                        <p className="text-xs text-slate-500">{driver.phone_number}</p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-purple-600 hover:bg-purple-700"
                        disabled={assigningDriver}
                        data-testid={`select-driver-${driver.id}`}
                      >
                        {assigningDriver ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Select"
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAssignModal(null)}
                  className="w-full"
                  disabled={assigningDriver}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Bulk Delete Confirmation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Are you sure you want to permanently delete <strong className="text-red-600">{selectedBookings.size}</strong> booking{selectedBookings.size > 1 ? 's' : ''}?
              </p>
              <p className="text-xs text-red-500 mb-4">
                This action cannot be undone.
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkDeleteModal(false)}
                  className="flex-1"
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleBulkDelete}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  disabled={bulkProcessing}
                  data-testid="confirm-bulk-delete-btn"
                >
                  {bulkProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete {selectedBookings.size} Bookings
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Assign Driver Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-600">
                <UserPlus className="w-5 h-5" />
                Bulk Assign Driver
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Assign a driver to <strong className="text-purple-600">{selectedBookings.size}</strong> booking{selectedBookings.size > 1 ? 's' : ''}
              </p>
              
              {drivers.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-500 mb-2">No active drivers available.</p>
                  <Link to="/admin/drivers">
                    <Button variant="outline" size="sm">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Drivers
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {drivers.map((driver) => (
                    <div 
                      key={driver.id}
                      className="flex items-center justify-between p-3 border rounded-md hover:bg-slate-50 cursor-pointer"
                      onClick={() => !bulkProcessing && handleBulkAssignDriver(driver.id)}
                    >
                      <div>
                        <p className="font-medium">{driver.first_name} {driver.last_name}</p>
                        <p className="text-xs text-slate-500">{driver.phone_number}</p>
                      </div>
                      <Button 
                        size="sm" 
                        className="bg-purple-600 hover:bg-purple-700"
                        disabled={bulkProcessing}
                      >
                        {bulkProcessing ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          "Select"
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkAssignModal(false)}
                  className="w-full"
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Status Update Modal */}
      {showBulkStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Bulk Update Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Update status for <strong>{selectedBookings.size}</strong> booking{selectedBookings.size > 1 ? 's' : ''}
              </p>
              
              <div className="space-y-2">
                {["pending", "confirmed", "assigned", "completed", "cancelled"].map((status) => (
                  <Button 
                    key={status}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleBulkUpdateStatus(status)}
                    disabled={bulkProcessing}
                  >
                    <Badge className={`mr-2 ${statusColors[status]}`}>{status}</Badge>
                    Set to {status}
                  </Button>
                ))}
              </div>
              
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkStatusModal(false)}
                  className="w-full"
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Payment Status Update Modal */}
      {showBulkPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Bulk Update Payment Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Update payment status for <strong>{selectedBookings.size}</strong> booking{selectedBookings.size > 1 ? 's' : ''}
              </p>
              
              <div className="space-y-2">
                {["pending", "paid", "paid_by_cash", "refunded"].map((status) => (
                  <Button 
                    key={status}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => handleBulkUpdatePaymentStatus(status)}
                    disabled={bulkProcessing}
                  >
                    <Badge className={`mr-2 ${paymentColors[status]}`}>{status}</Badge>
                    Set to {status}
                  </Button>
                ))}
              </div>
              
              <div className="mt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowBulkPaymentModal(false)}
                  className="w-full"
                  disabled={bulkProcessing}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
